import { showProviderCreditPrompt, isProviderCreditIssue } from './providerCredits';
export const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:8787';

// Fallback for browsers that block cross-site cookies (Safari, iPad): keep the signed session in localStorage.
const KEYS = { session: 'lumnos.session', site: 'lumnos.site' } as const;
const store = {
  get: (k: string) => { try { return localStorage.getItem(k) ?? ''; } catch { return ''; } },
  set: (k: string, v: string) => { try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch { /* ignore */ } },
};
{
  const m = /[#&]lt=([^&]+)/.exec(window.location.hash);
  if (m) { store.set(KEYS.session, decodeURIComponent(m[1])); history.replaceState(null, '', window.location.pathname + window.location.search); }
}
export function authHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  const s = store.get(KEYS.session), t = store.get(KEYS.site);
  if (s) h['x-lumnos-session'] = s;
  if (t) h['x-lumnos-site'] = t;
  return h;
}
function keep(res: Response) {
  const s = res.headers.get('x-lumnos-set-session'), t = res.headers.get('x-lumnos-set-site');
  if (s !== null) store.set(KEYS.session, s);
  if (t !== null) store.set(KEYS.site, t);
}
/** Adds the session to a file URL used in <img>/<a>, for browsers that block cookies. */
export function withAuth(url: string) { const t = store.get(KEYS.session); return t ? `${url}${url.includes('?') ? '&' : '?'}lt=${encodeURIComponent(t)}` : url; }
export function clearSessionToken() { store.set(KEYS.session, ''); }

/** Network errors (not HTTP errors) on reads are retried twice; flaky school/office networks drop requests. */
async function fetchRetry(url: string, method: string, init: RequestInit): Promise<Response> {
  const tries = method === 'GET' ? 3 : 1;
  for (let i = 0; ; i++) {
    try { return await fetch(url, init); } catch (e) {
      if (i + 1 >= tries || init.signal?.aborted) throw e;
      await new Promise((r) => setTimeout(r, 500 * (i + 1)));
    }
  }
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) { super(message); }
}

export async function api<T = any>(path: string, opts: { method?: string; body?: unknown; signal?: AbortSignal; raw?: boolean } = {}): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined ? 'POST' : 'GET');
  const isForm = opts.body instanceof FormData;
  const res = await fetchRetry(API_URL + path, method, {
    method,
    credentials: 'include',
    signal: opts.signal,
    headers: {
      ...authHeaders(),
      ...(method !== 'GET' ? { 'x-lumnos-csrf': '1' } : {}),
      ...(opts.body !== undefined && !isForm ? { 'content-type': 'application/json' } : {}),
    },
    body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
  });
  keep(res);
  if (!res.ok) {
    let code = 'error', message = `Request failed (${res.status})`;
    try { const j = await res.json(); code = j.error?.code ?? code; message = j.error?.message ?? message; if(code==='provider_credit_exhausted'&&isProviderCreditIssue(j.error))showProviderCreditPrompt(j.error); } catch { /* not json */ }
    throw new ApiError(res.status, code, message);
  }
  if (opts.raw) return res as unknown as T;
  const ct = res.headers.get('content-type') ?? '';
  const result=ct.includes('application/json')?await res.json():await res.text();
  const issue=result?.media?.options?.providerCreditError;
  if(isProviderCreditIssue(issue))showProviderCreditPrompt(issue,`media:${result.media.id}`);
  return result as T;
}

/** Upload with progress (fetch has no upload progress events). */
export function uploadFile(file: File, fields: Record<string, string | undefined>, onProgress?: (p: number) => void): Promise<any> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', API_URL + '/api/files');
    xhr.withCredentials = true;
    for (const [k, v] of Object.entries(authHeaders())) xhr.setRequestHeader(k, v);
    xhr.setRequestHeader('x-lumnos-csrf', '1');
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
    xhr.onload = () => {
      let j: any = null;
      try { j = JSON.parse(xhr.responseText); } catch { /* ignore */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(j.file);
      else reject(new ApiError(xhr.status, j?.error?.code ?? 'error', j?.error?.message ?? 'Upload failed'));
    };
    xhr.onerror = () => reject(new ApiError(0, 'network', 'Network error during upload'));
    const fd = new FormData();
    for (const [k, v] of Object.entries(fields)) if (v) fd.append(k, v);
    fd.append('file', file);
    xhr.send(fd);
  });
}

export async function download(path: string, fallbackName: string) {
  const res = await api<Response>(path, { raw: true });
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const name = decodeURIComponent(cd.match(/filename="?([^"]+)"?/)?.[1] ?? fallbackName);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
